<template>
  <div class="iso-image-results" @click="handlePaginationClick">
    <div v-if="isLoading" class="iso-image-results__overlay">
      <div class="iso-image-results__loader">
        <span class="iso-image-results__spinner" aria-hidden="true"></span>
        <span>Loading ISO image list...</span>
      </div>
    </div>

    <AppPagination
      :pagination="pagination"
      aria-label="ISO image pagination"
      key-prefix="iso-image-page"
    />

    <table class="table table-striped table-bordered table-condensed iso-image-table">
      <thead>
        <tr>
          <th class="iso-image-col iso-image-col--os"><div class="text-center">OS</div></th>
          <th class="iso-image-col iso-image-col--name"><div class="text-center">File Name</div></th>
          <th class="iso-image-col iso-image-col--size"><div class="text-center">Size</div></th>
          <th class="iso-image-col iso-image-col--date hidden-tablet"><div class="text-center">Uploaded Date</div></th>
          <th class="iso-image-col iso-image-col--owner"><div class="text-center">Owner</div></th>
          <th class="iso-image-col iso-image-col--actions iso-image-actions-col"><div class="text-center">Actions</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!paginatedRows.length && !isLoading">
          <td colspan="7" class="text-center iso-image-results__empty">No ISO images found.</td>
        </tr>
        <tr v-for="row in paginatedRows" :key="`iso-${row.id}`">
          <td><div class="text-center"><AppOsIcon :symbol="row.os_icon_symbol" /></div></td>
          <td>
            <button type="button" class="iso-image-name-link" @click="showDetail(row)">{{ row.filename }}</button>
            <div v-if="row.comment" class="iso-image-name-comment">{{ row.comment }}</div>
          </td>
          <td><div class="text-center">{{ row.size }}</div></td>
          <td class="hidden-tablet iso-image-date-cell">{{ row.updated_at }}</td>
          <td><div class="text-center">{{ row.owner }}</div></td>
          <td class="iso-image-actions-col">
            <div class="iso-image-more-actions">
              <button
                type="button"
                class="iso-image-more-actions__gear"
                :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
                @click.stop="toggleMenu(row.id, $event)"
              >
                <AppIcon name="settings" />
                <AppIcon name="chevron-down" class="iso-image-more-actions__caret" />
              </button>
              <div
                v-if="openMenuId === row.id"
                class="iso-image-more-actions__menu"
                :style="menuStyle"
                @click.stop
              >
                <a v-if="row.actions.download_url" :href="row.actions.download_url" class="iso-image-more-actions__item hidden-tablet">
                  <AppIcon name="download" />
                  <span>Download</span>
                </a>
                <a
                  v-if="row.actions.can_migrate"
                  :href="row.actions.migration_url"
                  class="iso-image-more-actions__item"
                >
                  <AppIcon name="disk" />
                  <span>{{ row.actions.migration_label }}</span>
                </a>
                <button
                  v-if="row.actions.can_delete"
                  type="button"
                  class="iso-image-more-actions__item iso-image-more-actions__item--danger"
                  @click="handleDelete(row)"
                >
                  <AppIcon name="delete" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <VmImageDetailModal
      modal-id="show-iso-image-detail"
      :open="detailOpen"
      :title="detailTitle"
      :detail-data="detailData"
      resource-label="ISO Image"
      @close="closeDetail"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import AppPagination from './AppPagination.vue'
import AppOsIcon from './AppOsIcon.vue'
import VmImageDetailModal from './VmImageDetailModal.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [] })
  },
  dataUrl: {
    type: String,
    default: ''
  },
  initialLoading: {
    type: Boolean,
    default: false
  }
})

const data = ref(props.initialData || { rows: [] })
const isLoading = ref(!!props.initialLoading)
const currentPage = ref(1)
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const openMenuId = ref(null)
const menuStyle = ref({})
const detailOpen = ref(false)
const detailTitle = ref('')
const detailData = ref(null)
const perPage = 20

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

const pagination = computed(() => {
  const totalCount = (data.value.rows || []).length
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
  const safeCurrentPage = Math.min(currentPage.value, totalPages)

  return {
    current_page: safeCurrentPage,
    total_pages: totalPages,
    prev_page: safeCurrentPage > 1 ? safeCurrentPage - 1 : null,
    next_page: safeCurrentPage < totalPages ? safeCurrentPage + 1 : null
  }
})

const paginatedRows = computed(() => {
  const offset = (pagination.value.current_page - 1) * perPage
  return (data.value.rows || []).slice(offset, offset + perPage)
})

watch(
  () => props.initialData,
  (nextValue) => {
    data.value = nextValue || { rows: [] }
    currentPage.value = 1
  }
)

watch(
  () => props.initialLoading,
  (nextValue) => {
    isLoading.value = !!nextValue
  }
)

function handleUpdate(event) {
  data.value = event.detail || { rows: [] }
  currentPage.value = 1
  isLoading.value = false
}

function handleLoading(event) {
  const payload = event.detail || {}
  if (payload.eventName !== 'scc:iso-image-results:update') {
    return
  }

  isLoading.value = !!payload.loading
}

function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  event.preventDefault()
  const page = Number(link.dataset.page)
  if (!Number.isFinite(page) || page < 1) {
    return
  }

  currentPage.value = page
}

function toggleMenu(rowId, event) {
  if (openMenuId.value === rowId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 182
  const menuHeight = 112
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) left = viewportWidth - menuWidth - 12
  if (left < 12) left = 12
  if (top + menuHeight > viewportHeight - 12) top = rect.top - menuHeight - gap
  if (top < 12) top = 12

  openMenuId.value = rowId
  menuStyle.value = { position: 'fixed', top: `${top}px`, left: `${left}px` }
}

function closeMenu() {
  openMenuId.value = null
  menuStyle.value = {}
}

async function loadRows() {
  if (!props.dataUrl) {
    return
  }

  isLoading.value = true
  const startedAt = Date.now()

  try {
    const response = await fetch(props.dataUrl, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    data.value = payload?.results_data || { rows: [] }
    currentPage.value = 1
  } finally {
    const elapsed = Date.now() - startedAt
    if (elapsed < 250) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 - elapsed))
    }
    isLoading.value = false
  }
}

async function handleDelete(row) {
  const actions = row.actions || {}
  if (!actions.destroy_url) {
    return
  }

  if (actions.destroy_confirm_message && !window.confirm(actions.destroy_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.destroy_url, { method: 'DELETE' })
    if (payload && payload.level && payload.message) {
      sccShowToast(payload.level, payload.message)
    }

    data.value = {
      ...data.value,
      rows: (data.value.rows || []).filter((entry) => String(entry.id) !== String(row.id))
    }
    closeMenu()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete ISO image.')
  }
}

async function showDetail(row) {
  if (!row.detail_url) return

  try {
    const payload = await requestJson(row.detail_url)
    detailTitle.value = payload.title || row.filename
    detailData.value = payload.detail_data || null
    detailOpen.value = true
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to load ISO image details.')
  }
}

function closeDetail() {
  detailOpen.value = false
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error((payload && payload.message) || text || 'Request failed.')
  }

  return payload
}

onMounted(() => {
  document.addEventListener('scc:iso-image-results:update', handleUpdate)
  document.addEventListener('scc:lazy-results:loading', handleLoading)
  document.addEventListener('click', handleDocumentClick)
  loadRows().catch((error) => {
    console.error('Failed to load ISO images', error)
    window.alert('Failed to load ISO images.')
  })
})

onBeforeUnmount(() => {
  document.removeEventListener('scc:iso-image-results:update', handleUpdate)
  document.removeEventListener('scc:lazy-results:loading', handleLoading)
  document.removeEventListener('click', handleDocumentClick)
})

function handleDocumentClick(event) {
  if (!event.target.closest('.iso-image-more-actions__menu') && !event.target.closest('.iso-image-more-actions__gear')) {
    closeMenu()
  }
}
</script>

<style scoped>
.iso-image-results {
  position: relative;
  min-height: 120px;
}

.iso-image-results__overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.iso-image-results__loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.iso-image-results__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: iso-image-results-spin 0.9s linear infinite;
}

.iso-image-results__empty {
  color: #64748b;
  padding: 24px 12px !important;
}

.iso-image-table {
  table-layout: fixed;
}

.iso-image-col--os {
  width: 56px;
}

.iso-image-col--name {
  width: 240px;
}

.iso-image-col--size {
  width: 96px;
}

.iso-image-col--date {
  width: 176px;
}

.iso-image-col--owner {
  width: 110px;
}

.iso-image-col--actions {
  width: 74px;
}

.iso-image-name-cell {
  text-align: left;
}

.iso-image-name-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #337ab7;
  font: inherit;
  font-weight: 600;
  text-align: left;
}

.iso-image-name-link:hover,
.iso-image-name-link:focus {
  color: #23527c;
  text-decoration: underline;
}

.iso-image-name-comment {
  margin-top: 4px;
  color: #6b7c8f;
  font-size: 12px;
  line-height: 1.35;
}

.iso-image-date-cell {
  white-space: nowrap;
}

.iso-image-date-cell .text-center {
  white-space: nowrap;
}

.iso-image-more-actions {
  position: relative;
}

.iso-image-more-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.iso-image-more-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.iso-image-more-actions__menu {
  z-index: 60;
  min-width: 182px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.iso-image-more-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.iso-image-more-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.iso-image-more-actions__item--danger {
  color: #9f1d2d;
}

.iso-image-more-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}

@keyframes iso-image-results-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
